<template>
    <section class="pt-10" id="destination" style="background: #f8f9fa;">
        <div class="container my-5">
            <h1 class="text-center mb-5">Attractions API Guide</h1>
            <img class="fade-in-image" style="width: 100%; height: 300px;"
                src="https://visitsrilankatours.com/images/scenic-sl.jpg" alt="Scenic Sri Lanka">
            <!-- API Documentation Section -->
            <div class="api-section mt-4">
                <!-- Account Required for API Purchase -->
                <div class="card p-3 mb-4">
                    <h4>1. Account Required for API Purchase</h4>
                    <p>To purchase the API, you must first create an account. After purchasing, you will receive a
                        unique <strong>userApiCode</strong> that must be included in the headers of your API requests to
                        authenticate.</p>
                </div>

                <!-- Getting Your userApiCode -->
                <div class="card p-3 mb-4">
                    <h4>2. Getting Your userApiCode</h4>
                    <p>After purchasing the API, you will receive a <strong>userApiCode</strong>. This code is required
                        to access the API. Include it in the request header for authentication.</p>
                    <p>Example of the userApiCode you will receive: <code>your_api_code_here</code></p>
                </div>

                <!-- API Endpoint Overview -->
                <div class="card p-3 mb-4">
                    <h4>3. API Endpoint Overview</h4>
                    <div><strong>URL:</strong> <code>http://127.0.0.1:8000/api/attractions</code></div>
                    <div><strong>Method:</strong> <code>GET</code></div>
                    <div><strong>Response:</strong> A list of places with package details, including filters for
                        country, province, city, category, and subcategory.</div>
                </div>

                <!-- Filter Options Guide -->
                <div class="card p-3 mb-4">
                    <h4>4. Filter Options Guide</h4>
                    <p>The API supports filtering results by the following fields:</p>
                    <ul>
                        <li><strong>Place Name:</strong> Filter places names. (e.g., Sigiriya Rock, 9Arch ridge, etc.).
                        </li>
                        <li><strong>Country:</strong> Filter places by country.</li>
                        <li><strong>Province:</strong> Filter places by province or district.</li>
                        <li><strong>City:</strong> Filter places by city.</li>
                        <li><strong>Category:</strong> Filter places by category (e.g., Beach, Mountain, etc.).</li>
                        <li><strong>Subcategory:</strong> Filter places by subcategory (e.g., White Sand, Hiking Trail,
                            etc.).</li>
                        <li><strong>Limit:</strong> Specify the maximum number of results to return. This is useful for
                            pagination.</li>
                        <li><strong>Offset:</strong> Specify the starting point for the results. Use this in combination
                            with limit for pagination.</li>
                    </ul>
                    <p>Example of how to apply filters with limit and offset:</p>
                    <pre><code>http://127.0.0.1:8000/api/attractions?country=Sri%20Lanka&category=Beach&province=Eastern%20Province&limit=10&offset=20</code></pre>
                    <p>In the above example, the API will return up to 10 places from Sri Lanka, under the "Beach"
                        category, located in the "Eastern Province", starting from the 21st result.</p>

                    <pre><code>http://127.0.0.1:8000/api/attractions?country=Sri Lanka&category=Beach&province=Eastern Province&limit=10&offset=20</code></pre>
                    <p>You can pass variable value with space also like : Sri Lanka, Eastern Province and etc. Our API
                        will handle that space issue in url.</p>



                </div>


                <!-- Making the API Request -->
                <div class="card p-3 mb-4">
                    <h4>5. Making the API Request</h4>
                    <p><strong>Using Postman or Insomnia:</strong></p>
                    <ul>
                        <li>Open Postman or Insomnia.</li>
                        <li>Select the <code>GET</code> request method.</li>
                        <li>Enter the URL with optional query parameters, e.g.,
                            <code>http://127.0.0.1:8000/api/attractions?country=Sri%20Lanka&category=Beach</code>
                        </li>
                        <li>Under the Authorization tab, choose <strong>Bearer Token</strong> and add the
                            <strong>userApiCode</strong> you received.
                        </li>
                        <li>Click <strong>Send</strong> to make the request.</li>
                    </ul>

                    <p><strong>Using curl Command:</strong></p>
                    <pre><code>curl -X GET \
  "http://127.0.0.1:8000/api/attractions?country=Sri%20Lanka&category=Beach" \
  -H "Authorization: Bearer your_api_code_here"</code></pre>
                </div>

                <!-- Interpreting the Response -->
                <div class="card p-3 mb-4">
                    <h4>6. Interpreting the Response</h4>
                    <div class="response-example">
                        <code>[
                            {
        "placeName": "Arugamba",
        "categoryName": "Beach",
        "subCategoryName": "White Sand",
        "fullTime": null,
        "slug": "Arugamba--slug",
        "placeAddress": "Beach Hut Road, Arugam Bay, Sri Lanka",
        "placePhNo": null,
        "email": null,
        "coverImage": "http://127.0.0.1/20240828195541arugambacoverImage.jpeg",
        "placeImages": [
            "http://127.0.0.1/20241016001736arugambaaditionalImage1.jpg",
            "http://127.0.0.1/20241016001736arugambaaditionalImage2.jpg",
            "http://127.0.0.1/20241016001736arugambaaditionalImage3.jpg",
            "http://127.0.0.1/20241016001736arugambaaditionalImage4.webp",
            "http://127.0.0.1/20241016001736arugambaaditionalImage5.jpg"
        ],
        "placeLattitude": "6.848717799999999",
        "placeLongtitude": "81.8310183",
        "country": "Sri Lanka",
        "district": "Eastern Province",
        "city": "Arugam Bay",
        "functionalDays": {
            "Monday": {
                "open": "00:00:00",
                "close": "00:00:00"
            },
            "Tuesday": {
                "open": "00:00:00",
                "close": "00:00:00"
            },
            "Wednesday": {
                "open": "00:00:00",
                "close": "00:00:00"
            },
            "Thursday": {
                "open": "00:00:00",
                "close": "00:00:00"
            },
            "Friday": {
                "open": "00:00:00",
                "close": "00:00:00"
            },
            "Saturday": {
                "open": "00:00:00",
                "close": "00:00:00"
            },
            "Sunday": {
                "open": "00:00:00",
                "close": "00:00:00"
            }
        }
    }
]</code>
                    </div>
                </div>

                <!-- Response Fields -->
                <div class="card p-3 mb-4">
                    <h4>7. Response Fields</h4>
                    <ul>
                        <li><strong>placeName:</strong> Name of the place (e.g., "Arugamba").</li>
                        <li><strong>categoryName:</strong> Category of the place (e.g., "Beach").</li>
                        <li><strong>subCategoryName:</strong> Sub-category of the place (e.g., "White Sand").</li>
                        <li><strong>fullTime:</strong> Availability status (e.g., "null" or undefined, but it could
                            represent an availability status such as "Available").</li>
                        <li><strong>slug:</strong> SEO-friendly URL identifier (e.g., "Arugamba--slug").</li>
                        <li><strong>coverImage:</strong> URL of the cover image (e.g.,
                            "http://127.0.0.1/20240828195541arugambacoverImage.jpeg").</li>
                        <li><strong>placeImages:</strong> Array of image URLs associated with the place (e.g., multiple
                            image URLs like "http://127.0.0.1/20241016001736arugambaaditionalImage1.jpg", etc.).</li>
                        <li><strong>placeLattitude:</strong> Latitude of the place (e.g., "6.848717799999999").</li>
                        <li><strong>placeLongtitude:</strong> Longitude of the place (e.g., "81.8310183").</li>
                        <li><strong>country:</strong> Country where the place is located (e.g., "Sri Lanka").</li>
                        <li><strong>district:</strong> District where the place is located (e.g., "Eastern Province").
                        </li>
                        <li><strong>city:</strong> City where the place is located (e.g., "Arugam Bay").</li>
                        <li><strong>placeAddress:</strong> Address of the place (e.g., "Beach Hut Road, Arugam Bay, Sri
                            Lanka").</li>
                        <li><strong>availableDates:</strong> List of available dates for booking (e.g., "null" or
                            missing in the provided data, could potentially be a list like ["2024-12-20",
                            "2024-12-21"]).</li>
                        <li><strong>placePhNo:</strong> Phone number of the place (e.g., "null" in the provided data).
                        </li>
                        <li><strong>email:</strong> Email address for the place (e.g., "null" in the provided data).
                        </li>
                        <li><strong>functionalDays:</strong> Days and hours of operation for the place, such as:
                            <ul>
                                <li><strong>Monday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the provided
                                    data).</li>
                                <li><strong>Tuesday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the provided
                                    data).</li>
                                <li><strong>Wednesday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the
                                    provided data).</li>
                                <li><strong>Thursday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the
                                    provided data).</li>
                                <li><strong>Friday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the provided
                                    data).</li>
                                <li><strong>Saturday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the
                                    provided data).</li>
                                <li><strong>Sunday:</strong> 00:00:00 - 00:00:00 (closed or not defined in the provided
                                    data).</li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <!-- Handle API Response in Your Application -->
                <div class="card p-3 mb-4">
                    <h4>8. Handle API Response in Your Application</h4>
                    <pre><code>fetch('http://127.0.0.1:8000/api/attractions', {
  headers: {
    'Authorization': 'Bearer your_api_code_here'
  }
})
.then(response => response.json())
.then(data => {
  console.log(data);  // Use the data in your app
})
.catch(error => console.error('Error:', error));</code></pre>
                </div>

                <!-- API Authentication -->
                <div class="card p-3 mb-4">
                    <h4>9. API Authentication (If Needed)</h4>
                    <p><strong>Using Postman/Insomnia:</strong></p>
                    <ul>
                        <li>Go to <strong>Authorization</strong> tab.</li>
                        <li>Choose <strong>Bearer Token</strong> and add your <strong>userApiCode</strong>.</li>
                    </ul>

                    <p><strong>Using curl with Token:</strong></p>
                    <pre><code>curl -X GET \
  http://127.0.0.1:8000/api/attractions \
  -H "Authorization: Bearer your_api_code_here"</code></pre>
                </div>

                <!-- Troubleshooting -->
                <div class="card p-3 mb-4">
                    <h4>10. Troubleshooting</h4>
                    <ul>
                        <li><strong>500 Internal Server Error:</strong> Ensure the server is running correctly.</li>
                        <li><strong>404 Not Found:</strong> Check that the URL is correct.</li>
                        <li><strong>Unauthorized Error:</strong> Ensure the <strong>userApiCode</strong> is valid and
                            included in the header.</li>
                    </ul>
                </div>

                <!-- Example API Methods in Other Languages -->
                <div class="card p-3 mb-4">
                    <h4>11. Example API Methods in Other Languages</h4>

                    <h5 class="mt-3">C#</h5>
                    <pre><code>using System.Net.Http;
using System.Threading.Tasks;

var client = new HttpClient();
client.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_code_here");

var response = await client.GetAsync("http://127.0.0.1:8000/api/attractions");
var data = await response.Content.ReadAsStringAsync();
Console.WriteLine(data);</code></pre>

                    <h5 class="mt-5">PHP</h5>
                    <pre><code>$url = "http://127.0.0.1:8000/api/attractions";
$headers = [
    "Authorization: Bearer your_api_code_here"
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
$response = curl_exec($ch);
curl_close($ch);

echo $response;</code></pre>

                    <h5 class="mt-5">Python</h5>
                    <pre><code>import requests

url = "http://127.0.0.1:8000/api/attractions"
headers = {
    "Authorization": "Bearer your_api_code_here"
}

response = requests.get(url, headers=headers)
print(response.json())</code></pre>

                    <h5 class="mt-5">JavaScript (Node.js)</h5>
                    <pre><code>const fetch = require('node-fetch');

const url = "http://127.0.0.1:8000/api/attractions";
const headers = {
    "Authorization": "Bearer your_api_code_here"
};

fetch(url, { headers })
    .then(response => response.json())
    .then(data => console.log(data))
    .catch(error => console.error('Error:', error));</code></pre>
                </div>
            </div>
        </div>
    </section>
    <div class="container" id="packagesApi">
        <div class="px-4 py-5 px-md-5 text-center text-lg-start">
            <div class="container">
                <div class="row gx-lg-5 align-items-center">
                    <h3 class="mb-4 text-center text-uppercase fw-bold text-primary">
                        Choose Your Plan
                    </h3>
                    <h4 class="mb-1 text-center text-sec">
                        Improve your business with our outstanding destinations APIs.
                    </h4>
                    <div class="col-lg-12">
                        <div class="" style="background-color: #ffffff; border-radius: 15px;">
                            <div class="card-body py-5 px-md-5">

                                <div v-if="packages.length > 0" class="row gy-4">
                                    <!-- Package Card -->
                                    <div v-for="(pkg, index) in packages" :key="index"
                                        class="col-md-12 col-lg-6 d-flex align-items-stretch">
                                        <div class="package-card p-4 border rounded shadow-sm w-100">
                                            <h1 class="text-primary text-uppercase fw-bold text-center">
                                                {{ pkg.name }}
                                            </h1>
                                            <p class="text-center fs-3">
                                                <strong class="text-sec">${{ pkg.price }}</strong> / month
                                            </p>
                                            <div class="d-flex justify-content-center">
                                                <!-- Button will be disabled and active if user matches -->
                                                <button class="col-12 btn btn-lg text-uppercase px-4 mt-3" :class="{
                                                    'btn-success': isUserMatch(pkg),
                                                    'btn-primary': !isUserMatch(pkg)
                                                }" :disabled="isUserMatch(pkg)" @click="goToPurchse(pkg)">
                                                    <span v-if="isUserMatch(pkg)">Already Purchased</span>
                                                    <span v-else>Purchase</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-center">
                                    <p class="text-muted fs-5">No packages available at the moment.</p>
                                </div>
                                <div class="alert alert-danger mt-3" v-if="error">
                                    {{ error }}
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    </div>
</template>




<script>
import Swal from "sweetalert2";
import $ from 'jquery';
import { loadStripe } from '@stripe/stripe-js';
console.log(process.env.VUE_Stripe_Key);

export default {
    name: 'ApiGuide',
    data() {
        return {
            packages: [], // Holds fetched packages
            error: null,  // Holds error messages
            purchasedPackages: [] // Array to track purchased packages
        };
    },
    mounted() {
        this.fetchPackages();
    },
    methods: {
        // Check if the user ID from localStorage matches any userId in the userList of the package
        isUserMatch(pkg) {
            const user = JSON.parse(localStorage.getItem("user"));
            if (user && pkg.userIds) {
                const userList = pkg.userIds; // Already an array of strings
                return userList.includes(user.id.toString()); // Check if user.id exists in the list
            }
            return false;
        },

        // Check if the purchase button is disabled (based on whether the package has been purchased)
        isButtonDisabled(pkg) {
            return this.purchasedPackages.includes(pkg.id);
        },

        handleGetApiPackage() {
            // Check if user data exists in localStorage
            const user = JSON.parse(localStorage.getItem("user"));

            if (user) {
                // User is logged in, redirect to the /apiPackage route
                window.location.href = "/apiPackage";
            } else {
                // Show SweetAlert notification
                Swal.fire({
                    title: "Unauthorized!",
                    text: "Redirecting to login page...",
                    icon: "warning",
                    timer: 2000, // Automatically close after 2 seconds
                    showConfirmButton: false, // Hide confirmation button
                    allowOutsideClick: false, // Prevent closing by clicking outside
                }).then(() => {
                    // Redirect to login page after 2 seconds
                    localStorage.setItem("redirectAfterLogin", "/apiDoc#packagesApi");
                    window.location.href = "/login";
                });
            }
        },

        fetchPackages() {
            const apiUrl = process.env.VUE_APP_API_URL +'/getEnableApiPackages';
            $.ajax({
                url: apiUrl,
                method: 'GET',
                success: (response) => {
                    this.packages = response;
                },
                error: (xhr) => {
                    this.error = 'Error fetching packages. Please try again later.';
                    console.error(xhr.responseJSON);
                },
            });
        },

        // Navigate to the plan details page
        goToPurchse(pkg) {
            // Prevent multiple purchases for the same package
            if (this.isButtonDisabled(pkg)) {
                return;
            }

            // Check if user data exists in localStorage
            const user = JSON.parse(localStorage.getItem("user"));
            if (user) {
                const stripe = Stripe(process.env.VUE_Stripe_Key); // Your Stripe public key
                
                const price = pkg.price;
                const name = pkg.name;
                const userId = user.id;
                const successUrl = process.env.Vue_APP_URL +'/pay-success'; // Vue success URL
                const cancelUrl = process.env.Vue_APP_URL +'/pay-fail'; // Vue failure URL

                // Make an AJAX request to your backend to create a Checkout session
                $.ajax({
                   url: process.env.VUE_APP_API_URL +'/create-api-package-checkout-session',
                    method: 'POST',
                    data: {
                        id: userId,
                        price: price,
                        name: name,
                        success_url: successUrl, // Pass the success URL
                        cancel_url: cancelUrl,   // Pass the failure URL
                    },
                    success: (response) => {
                        // Mark the package as purchased
                        this.purchasedPackages.push(pkg.id);
                        // Redirect to Stripe Checkout
                        stripe.redirectToCheckout({ sessionId: response.sessionId }).then((result) => {
                            if (result.error) {
                                Swal.fire({
                                    title: 'Payment Failed',
                                    text: result.error.message,
                                    icon: 'error',
                                    confirmButtonText: 'Retry',
                                }).then(() => {
                                    this.$router.push('/payment-failure'); // Navigate to the failure page
                                });
                            }
                        });
                    },
                    error: (error) => {
                        console.error('Error creating checkout session:', error);
                        Swal.fire({
                            title: 'Error',
                            text: 'An error occurred while processing your payment.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                        });
                    }
                });
            } else {
                // Show SweetAlert notification
                Swal.fire({
                    title: "Unauthorized!",
                    text: "Redirecting to login page...",
                    icon: "warning",
                    timer: 2000, // Automatically close after 2 seconds
                    showConfirmButton: false, // Hide confirmation button
                    allowOutsideClick: false, // Prevent closing by clicking outside
                }).then(() => {
                    // Redirect to login page after 2 seconds
                    localStorage.setItem("redirectAfterLogin", "/apiDoc#packagesApi");
                    window.location.href = "/login";
                });
            }
        }
    }
};
</script>

<style scoped>
.api-section {
    margin-bottom: 40px;
}

.api-endpoint {
    background-color: #f8f9fa;
    padding: 10px;
    border-radius: 5px;
    margin-bottom: 10px;
}

.response-example {
    background-color: #f1f1f1;
    padding: 10px;
    border-radius: 5px;
    margin-top: 10px;
    font-family: monospace;
}

.card {
    border: none;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.package-card {
    background: #f8f9fa;
    transition: transform 0.3s, box-shadow 0.3s;
}

.package-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
}

h4 {
    color: #5a5a5a;
}

button {
    background-color: #37a2d3;
    border: none;
    transition: background-color 0.3s;
}

button:hover {
    background-color: #bd0606;
    color: #ffffff;
}

.alert {
    font-size: 0.9rem;
}

.text-primary {
    color: #bd0606 !important;
}

.text-sec {
    color: #37a2d3 !important;
}

.package-card button {
    transition: background-color 0.3s;
}

.package-card button.btn-success {
    background-color: #28a745;
    /* Green for active button */
    pointer-events: none;
    /* Disable click */
}

.package-card button.btn-primary {
    background-color: #007bff;
    /* Default primary button */
}

.package-card button:disabled {
    background-color: #28a745 !important;
    /* Grey out disabled button */
    cursor: not-allowed;
}

.fade-in-image {
    position: relative;
    width: 100%;
    height: 300px;
    animation: fadeIn 1.5s ease-in-out forwards;
}

.fade-in-image::before,
.fade-in-image::after,
.fade-in-image::left,
.fade-in-image::right {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    transition: all 0.5s ease;
}

/* Fade-in effect from 4 sides */
@keyframes fadeIn {
    0% {
        opacity: 0;
        transform: scale(1.1);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}
</style>